import { createI18n, whenI18nReady } from '@platzona/i18n';
import i18next from 'i18next';

type TranslationDict = Record<string, unknown>;
type LocaleLoader = () => Promise<{ default: TranslationDict }>;

const localeModules = import.meta.glob<{ default: TranslationDict }>('./locales/*/translation.json');
const faqModules = import.meta.glob<{ default: TranslationDict }>('./i18n/saasFaqItems.*.json');
const localeLoaders: Record<string, LocaleLoader> = {};
const faqLoaders: Record<string, LocaleLoader> = {};
for (const [filePath, loader] of Object.entries(localeModules)) {
  const match = filePath.match(/locales\/([^/]+)\/translation\.json$/);
  if (match) localeLoaders[match[1]] = loader;
}
for (const [filePath, loader] of Object.entries(faqModules)) {
  const match = filePath.match(/saasFaqItems\.(.+)\.json$/);
  if (match) faqLoaders[match[1]] = loader;
}

createI18n({ resources: {} });
const loadedLocales = new Set<string>();
const loadedFaqLocales = new Set<string>();
let isInitialized = false;

function getLocaleCandidates(lng: string): string[] {
  const base = lng.split('-')[0];
  return Array.from(new Set([lng, base, 'en']));
}

async function ensureLocaleLoaded(lng: string) {
  // Always keep 'en' loaded — fallbackLng: 'en' is a no-op unless the en bundle
  // is resident. Missing keys in other locales otherwise render as raw "project.viewDraftPreview".
  if (!loadedLocales.has('en')) {
    const enLoader = localeLoaders.en;
    if (enLoader) {
      const mod = await enLoader();
      i18next.addResourceBundle('en', 'translation', mod.default, true, true);
      loadedLocales.add('en');
    }
  }

  const targetLocale = getLocaleCandidates(lng).find((code) => code in localeLoaders) ?? 'en';
  if (!loadedLocales.has(targetLocale)) {
    const loader = localeLoaders[targetLocale];
    if (loader) {
      const mod = await loader();
      i18next.addResourceBundle(targetLocale, 'translation', mod.default, true, true);
      loadedLocales.add(targetLocale);
    }
  }

  if (!loadedFaqLocales.has('en')) {
    const enFaqLoader = faqLoaders.en;
    if (enFaqLoader) {
      const mod = await enFaqLoader();
      i18next.addResourceBundle('en', 'translation', { faq: { items: mod.default } }, true, true);
      loadedFaqLocales.add('en');
    }
  }

  const faqLocale = getLocaleCandidates(lng).find((code) => code in faqLoaders) ?? 'en';
  if (!loadedFaqLocales.has(faqLocale)) {
    const loader = faqLoaders[faqLocale];
    if (loader) {
      const mod = await loader();
      i18next.addResourceBundle(faqLocale, 'translation', { faq: { items: mod.default } }, true, true);
      loadedFaqLocales.add(faqLocale);
    }
  }
}

export async function initI18n() {
  if (isInitialized) return;
  await whenI18nReady();
  await ensureLocaleLoaded(i18next.language || 'en');
  i18next.on('languageChanged', (lng) => {
    void ensureLocaleLoaded(lng);
  });
  isInitialized = true;
}
